import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

/** Matches `.scan-aim-frame` in shopping-home.component.css. */
export const BARCODE_AIM_WIDTH_RATIO = 0.86;
export const BARCODE_AIM_HEIGHT_RATIO = 0.22;

/** Target gap between the start of one read and the next, when a read finishes early. */
export const BARCODE_SCAN_INTERVAL_MS = 100;

const RETAIL_BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e'] as const;

const CENTER_POINT = [{ x: 0.5, y: 0.5 }];

export interface BarcodeCrop {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

interface BarcodeTrackCapabilities {
  focusMode?: string[];
  exposureMode?: string[];
  pointsOfInterest?: boolean;
  torch?: boolean;
}

interface DetectedBarcode {
  rawValue?: string;
}

export interface RetailBarcodeDetector {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): RetailBarcodeDetector;
  getSupportedFormats?: () => Promise<string[]>;
}

export function barcodeVideoConstraints(): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1920 },
      height: { ideal: 1080 }
    }
  };
}

export function readTrackCapabilities(track: MediaStreamTrack): BarcodeTrackCapabilities {
  const getCapabilities = track.getCapabilities as
    | undefined
    | (() => BarcodeTrackCapabilities);
  if (typeof getCapabilities !== 'function') {
    return {};
  }

  try {
    return getCapabilities.call(track) ?? {};
  } catch {
    return {};
  }
}

/** Focus and exposure steps the track claims to support. Unsupported ones are omitted. */
export function barcodeFocusConstraints(
  capabilities: BarcodeTrackCapabilities
): MediaTrackConstraints[] {
  const steps: MediaTrackConstraints[] = [];

  if (capabilities.focusMode?.includes('continuous')) {
    steps.push({
      advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet]
    });
  }

  if (capabilities.exposureMode?.includes('continuous')) {
    steps.push({
      advanced: [{ exposureMode: 'continuous' } as MediaTrackConstraintSet]
    });
  }

  if (capabilities.pointsOfInterest) {
    steps.push({
      advanced: [{ pointsOfInterest: CENTER_POINT } as MediaTrackConstraintSet]
    });
  }

  return steps;
}

export async function applyTrackConstraints(
  track: MediaStreamTrack,
  steps: MediaTrackConstraints[]
): Promise<void> {
  for (const step of steps) {
    try {
      await track.applyConstraints(step);
    } catch {
      // This phone rejected the feature. Keep scanning with the rest.
    }
  }
}

export async function tuneBarcodeTrack(
  track: MediaStreamTrack
): Promise<{ torch: boolean }> {
  const capabilities = readTrackCapabilities(track);
  await applyTrackConstraints(track, barcodeFocusConstraints(capabilities));
  return { torch: capabilities.torch === true };
}

export function torchConstraintSteps(on: boolean): MediaTrackConstraints[] {
  const flash = on ? 'flash' : 'off';
  return [
    {
      advanced: [{ torch: on, fillLightMode: flash } as MediaTrackConstraintSet]
    },
    { advanced: [{ torch: on } as MediaTrackConstraintSet] },
    { advanced: [{ fillLightMode: flash } as MediaTrackConstraintSet] }
  ];
}

/** Turns the torch on or off. Returns false when every form of the constraint is rejected. */
export async function setBarcodeTorch(
  track: MediaStreamTrack,
  on: boolean
): Promise<boolean> {
  for (const step of torchConstraintSteps(on)) {
    try {
      await track.applyConstraints(step);
      return true;
    } catch {
      // try the next form
    }
  }
  return false;
}

/**
 * Source rectangle for the on-screen aim frame.
 * Uses the same cover-crop math as `object-fit: cover` so the decoded band
 * is the band the shopper sees inside the frame.
 */
export function centerBandCrop(
  videoWidth: number,
  videoHeight: number,
  elementWidth: number,
  elementHeight: number
): BarcodeCrop | null {
  if (videoWidth < 2 || videoHeight < 2) {
    return null;
  }

  let visibleX = 0;
  let visibleY = 0;
  let visibleW = videoWidth;
  let visibleH = videoHeight;

  if (elementWidth > 0 && elementHeight > 0) {
    const scale = Math.max(elementWidth / videoWidth, elementHeight / videoHeight);
    visibleW = elementWidth / scale;
    visibleH = elementHeight / scale;
    visibleX = (videoWidth - visibleW) / 2;
    visibleY = (videoHeight - visibleH) / 2;
  }

  const sw = visibleW * BARCODE_AIM_WIDTH_RATIO;
  const sh = visibleH * BARCODE_AIM_HEIGHT_RATIO;
  let sx = visibleX + (visibleW - sw) / 2;
  let sy = visibleY + (visibleH - sh) / 2;

  sx = Math.max(0, Math.min(Math.round(sx), videoWidth - 1));
  sy = Math.max(0, Math.min(Math.round(sy), videoHeight - 1));
  const width = Math.max(1, Math.min(Math.round(sw), videoWidth - sx));
  const height = Math.max(1, Math.min(Math.round(sh), videoHeight - sy));

  return { sx, sy, sw: width, sh: height };
}

export function drawBarcodeBand(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement
): boolean {
  const crop = centerBandCrop(
    video.videoWidth,
    video.videoHeight,
    video.clientWidth,
    video.clientHeight
  );
  if (!crop) {
    return false;
  }

  if (canvas.width !== crop.sw || canvas.height !== crop.sh) {
    canvas.width = crop.sw;
    canvas.height = crop.sh;
  }

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return false;
  }

  ctx.drawImage(
    video,
    crop.sx,
    crop.sy,
    crop.sw,
    crop.sh,
    0,
    0,
    crop.sw,
    crop.sh
  );
  return true;
}

export function createRetailBarcodeReader(): BrowserMultiFormatReader {
  const hints = new Map<DecodeHintType, unknown>();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E
  ]);
  return new BrowserMultiFormatReader(hints);
}

export async function createRetailBarcodeDetector(): Promise<RetailBarcodeDetector | null> {
  const Detector = (
    globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }
  ).BarcodeDetector;
  if (!Detector) {
    return null;
  }

  try {
    let formats: string[] = [...RETAIL_BARCODE_FORMATS];
    if (typeof Detector.getSupportedFormats === 'function') {
      const supported = await Detector.getSupportedFormats();
      formats = formats.filter((format) => supported.includes(format));
      if (formats.length === 0) {
        return null;
      }
    }
    return new Detector({ formats });
  } catch {
    return null;
  }
}

export interface BarcodeReadStep {
  pending: string | null;
  confirmed: string | null;
}

/** One read is remembered. The same code again is accepted. A blank keeps the pending code. */
export function nextBarcodeCandidate(
  pending: string | null,
  read: string | null
): BarcodeReadStep {
  const code = read?.trim() ?? '';
  if (code.length === 0) {
    return { pending, confirmed: null };
  }
  if (pending === code) {
    return { pending: null, confirmed: code };
  }
  return { pending: code, confirmed: null };
}

export async function readRetailBarcode(
  canvas: HTMLCanvasElement,
  detector: RetailBarcodeDetector | null,
  reader: BrowserMultiFormatReader | null
): Promise<string | null> {
  if (detector) {
    try {
      const codes = await detector.detect(canvas);
      const code = codes
        .map((item) => (item.rawValue ?? '').trim())
        .find((value) => value.length > 0);
      return code ?? null;
    } catch {
      return null;
    }
  }

  if (!reader) {
    return null;
  }

  try {
    const text = reader.decodeFromCanvas(canvas).getText().trim();
    return text || null;
  } catch {
    return null;
  }
}
